import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { PageHeader, StatCard, StatusBadge, Logo, money, fmtDate } from "@/components/common";
import { FulfillmentOverview } from "@/components/FulfillmentOverview";
import { DashboardWidgets, W } from "@/components/DashboardWidgets";
import { ReportsTeam } from "@/pages/ReportsTeam";
import { usePeriod, PeriodFilter } from "@/components/PeriodFilter";
import { ClientLogistics } from "@/components/reports/LogisticsTab";
import { Button } from "@/components/ui/button";
import { HeroUIProvider } from "@heroui/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import {
  Package, Truck, Receipt, ClipboardText, Buildings, Factory, CurrencyDollar,
  ClockCountdown, Plus, CheckCircle, ChartBar, TrendUp, PaperPlaneTilt, Pill, Percent,
} from "@phosphor-icons/react";

export default function Reports() {
  const { user, isTeam } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [clientSales, setClientSales] = useState([]);
  const [profit, setProfit] = useState(null);
  const [shipModes, setShipModes] = useState(null);
  const [topItems, setTopItems] = useState([]);
  const [showClients, setShowClients] = useState(false);
  const [showAllProfit, setShowAllProfit] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [shipments, setShipments] = useState([]);
  const isAdmin = user?.role === "admin";
  const isOps = user?.email === "ops@heliopolispharmaceuticals.com";

  const period = usePeriod();
  const qs = `?from=${period.range.from}&to=${period.range.to}`;
  const pqs = `?from=${period.prev.from}&to=${period.prev.to}`;
  const [prev, setPrev] = useState({});
  const [analytics, setAnalytics] = useState({});

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/orders").then((r) => { setOrders(r.data.slice(0, 6)); setAllOrders(r.data); }).catch(() => {});
    if (!isTeam) api.get("/shipments").then((r) => setShipments(r.data)).catch(() => {});
  }, [isAdmin, isOps, isTeam]);

  useEffect(() => {
    setAnalytics({});
    const an = (key, path) => api.get(`/analytics/${path}${qs}`).then((r) => setAnalytics((a) => ({ ...a, [key]: r.data }))).catch(() => {});
    const pv = (key, path) => api.get(`/analytics/${path}${pqs}`).then((r) => setPrev((p) => ({ ...p, [key]: r.data }))).catch(() => {});
    an("logistics", "logistics");
    if (isTeam) { an("activity", "client-activity"); pv("logistics", "logistics"); pv("activity", "client-activity"); }
    if (isAdmin) { an("logisticsProfit", "logistics-profit"); pv("logisticsProfit", "logistics-profit"); }
    api.get(`/analytics/monthly-sales${qs}`).then((r) => setMonthly(r.data)).catch(() => {});
    if (isAdmin) api.get(`/analytics/client-sales${qs}`).then((r) => setClientSales(r.data)).catch(() => {});
    if (isAdmin) api.get(`/analytics/profit${qs}`).then((r) => setProfit(r.data)).catch(() => {});
    if (isAdmin) api.get(`/analytics/shipment-modes${qs}`).then((r) => setShipModes(r.data)).catch(() => {});
    if (isAdmin) api.get(`/analytics/top-items${qs}`).then((r) => setTopItems(r.data)).catch(() => {});
    if (isTeam) {
      setPrev({});
      api.get(`/analytics/monthly-sales${pqs}`).then((r) => setPrev((p) => ({ ...p, monthly: r.data }))).catch(() => {});
      if (isAdmin) api.get(`/analytics/profit${pqs}`).then((r) => setPrev((p) => ({ ...p, profit: r.data }))).catch(() => {});
      if (isAdmin) api.get(`/analytics/shipment-modes${pqs}`).then((r) => setPrev((p) => ({ ...p, shipModes: r.data }))).catch(() => {});
    }
  }, [isAdmin, isTeam, qs, pqs]);

  const [readyAll, setReadyAll] = useState(false);
  const readyRows = !isTeam ? (() => {
    const active = allOrders.filter((o) => o.status === "approved" && !["dispatched", "in_transit", "delivered", "arrived"].includes(o.shipping_status));
    const byOrder = new Map(active.map((o) => [o.id, o]));
    const rows = shipments
      .filter((s) => !["dispatched", "in_transit", "arrived", "delivered"].includes(s.status))
      .map((s) => {
        const ids = s.order_ids || [s.order_id];
        const os = ids.map((id) => allOrders.find((o) => o.id === id)).filter(Boolean);
        ids.forEach((id) => byOrder.delete(id));
        const items = s.is_bulk && s.line_items?.length ? s.line_items : os.flatMap((o) => o.items || []);
        const ptn = os.map((o) => o.patient_name).filter(Boolean).join(", ");
        const ready = os.length > 0 && os.every((o) => o.procurement_status === "received");
        const hasAwb = (s.awb_documents || []).length > 0;
        const stage = s.dispatch_hold?.active ? "on_hold" : hasAwb ? (s.awb_confirmed ? "awaiting_dispatch" : "awaiting_confirmation") : (ready ? "ready" : "preparing");
        return { id: s.id, link: `/shipments/${s.id}`, shipment_number: s.shipment_number, order_number: s.order_number, ptn, items, stage };
      });
    byOrder.forEach((o) => { if (o.procurement_status === "received") rows.push({ id: o.id, link: `/orders/${o.id}`, shipment_number: null, order_number: o.order_number, ptn: o.patient_name || "", items: o.items || [], stage: "ready" }); });
    const rank = { on_hold: 4, awaiting_dispatch: 3, ready: 2, awaiting_confirmation: 1, preparing: 0 };
    return rows.sort((a, b) => (rank[b.stage] || 0) - (rank[a.stage] || 0));
  })() : [];

  if (isTeam) {
    return (
      <div className="space-y-8" data-testid="reports-page">
        <PageHeader subtitle={`Welcome, ${user?.name?.split(" ")[0] || ""}`} title="Reports & Analytics" />
        <ReportsTeam stats={stats} monthly={monthly} clientSales={clientSales} profit={profit} shipModes={shipModes} topItems={topItems} orders={orders} isAdmin={isAdmin} period={period} prev={prev} analytics={analytics} onAliasesChanged={() => api.get(`/analytics/top-items${qs}`).then((r) => setTopItems(r.data)).catch(() => {})} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        subtitle={isTeam ? `Welcome, ${user?.name?.split(" ")[0] || ""}` : `${t("dash.welcome")}, ${user?.name?.split(" ")[0] || ""}`}
        title={isTeam ? "Reports & Analytics" : t("dash.yourPortal")}
        actions={
          !isTeam && (
            <Button className="rounded-sm" onClick={() => navigate("/orders/new")} data-testid="dash-place-order">
              <Plus size={16} className="mr-2" /> {t("nav.placeOrder")}
            </Button>
          )
        }
      />

      <DashboardWidgets>
      <W id="brief" title="Company brief">
      <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-card p-6 sm:p-8" data-testid="company-brief">
        <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-accent/15 blur-3xl" />
        <div className="absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-primary/15 blur-3xl" />
        <div className="relative flex flex-wrap items-start justify-between gap-6">
          <div className="max-w-2xl flex items-start gap-5">
            <Logo variant="mark" className="hidden sm:block h-16 w-16 object-contain shrink-0" />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">{t("login.heroTag")}</p>
              <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">{t("login.heroTitle")}</h2>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{t("login.heroBody")}</p>
            </div>
          </div>
          <div className="text-sm">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Contact</p>
            <a href="mailto:ops@heliopolispharmaceuticals.com" className="font-semibold text-primary hover:underline" data-testid="company-contact-email">ops@heliopolispharmaceuticals.com</a>
          </div>
        </div>
      </div>
      </W>

      <W id="stats" title="Key figures">
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {isTeam ? (
            <>
              <StatCard testid="stat-total-orders" label="Total Orders" value={stats.total_orders} icon={Package} />
              <StatCard testid="stat-pending" label="Pending Approval" value={stats.pending_approval} icon={ClockCountdown} tone="gold" />
              <StatCard testid="stat-transit" label="In Transit" value={stats.in_transit} icon={Truck} />
              <StatCard testid="stat-completed" label="Completed Orders" value={stats.completed_orders} icon={CheckCircle} tone="green" />
              <StatCard testid="stat-clients" label="Clients" value={stats.total_clients} icon={Buildings} />
              <StatCard testid="stat-open-pos" label="Open POs" value={stats.open_pos} icon={ClipboardText} />
              <StatCard testid="stat-revenue" label="Revenue (Paid)" value={money(stats.revenue)} icon={CurrencyDollar} tone="green" />
              <StatCard testid="stat-outstanding" label="Outstanding" value={money(stats.outstanding)} icon={Receipt} tone="red" />
            </>
          ) : (
            <>
              <StatCard testid="stat-total-sales" label={t("dash.statTotalSales")} value={money(stats.total_sales)} icon={CurrencyDollar} tone="green" />
              <StatCard testid="stat-outstanding" label={t("dash.statOutstanding")} value={money(stats.outstanding)} icon={Receipt} tone="red" />
              <StatCard testid="stat-total-orders" label={t("dash.statTotalOrders")} value={stats.total_orders} icon={Package} />
              <StatCard testid="stat-completed" label={t("dash.statCompleted")} value={stats.completed_orders} icon={CheckCircle} tone="green" />
              <StatCard testid="stat-pending" label={t("dash.statPending")} value={stats.pending_orders} icon={ClockCountdown} tone="gold" />
              <StatCard testid="stat-transit" label={t("dash.statInTransit")} value={stats.in_transit} icon={Truck} />
            </>
          )}
        </div>
      )}

      </W>

      {stats && !isTeam && <W id="fulfillment" title="Order fulfillment overview"><FulfillmentOverview refreshKey={allOrders.length} /></W>}

      {!isTeam && <W id="my-logistics" title="My logistics">
        <div className="space-y-4" data-testid="client-my-logistics">
          <div className="flex flex-wrap items-center justify-between gap-3"><div><h2 className="font-heading text-xl font-bold tracking-tight">My logistics</h2><p className="text-xs text-muted-foreground mt-1">How fast your shipments move: approved → dispatched → delivered.</p></div><HeroUIProvider><PeriodFilter {...period} /></HeroUIProvider></div>
          <HeroUIProvider><ClientLogistics data={analytics.logistics} periodLabel={period.periodLabel} /></HeroUIProvider>
        </div>
      </W>}

      <W id="monthly" title="Sales report (monthly & custom period)">
      <div className="hp-card p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <ChartBar size={20} className="text-primary" weight="duotone" /> Monthly Sales
          </h2>
          {isAdmin && (
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setShowClients((v) => !v)} data-testid="toggle-client-sales">
              {showClients ? "Hide client breakdown" : "Evaluate sales by client"}
            </Button>
          )}
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={monthly} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e4e4e7" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#e4e4e7" }} />
            <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={64} tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)} />
            <Tooltip formatter={(v) => money(v)} cursor={{ fill: "rgba(30,127,212,0.06)" }} />
            <Bar dataKey="total" fill="#1E7FD4" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {isAdmin && showClients && (
        <div className="hp-card p-6" data-testid="client-sales-section">
          <h2 className="font-heading text-xl font-bold tracking-tight mb-5">Sales by Client</h2>
          {clientSales.length === 0 ? (
            <p className="text-sm text-muted-foreground">No invoiced sales yet.</p>
          ) : (
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              <ResponsiveContainer width="100%" height={Math.max(200, clientSales.length * 48)}>
                <BarChart layout="vertical" data={clientSales} margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e4e4e7" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)} />
                  <YAxis type="category" dataKey="client_name" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={140} />
                  <Tooltip formatter={(v) => money(v)} cursor={{ fill: "rgba(245,185,33,0.08)" }} />
                  <Bar dataKey="total" fill="#F5B921" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="border border-border rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider font-semibold text-muted-foreground bg-muted/40">
                      <th className="px-4 py-2 font-semibold">Client</th>
                      <th className="px-4 py-2 font-semibold text-right">Invoices</th>
                      <th className="px-4 py-2 font-semibold text-right">Paid</th>
                      <th className="px-4 py-2 font-semibold text-right">Outstanding</th>
                      <th className="px-4 py-2 font-semibold text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientSales.map((c, i) => (
                      <tr key={c.client_id} className={i % 2 ? "bg-muted/30" : ""} data-testid={`client-sales-row-${c.client_name}`}>
                        <td className="px-4 py-2 font-medium">{c.client_name}</td>
                        <td className="px-4 py-2 text-right text-muted-foreground">{c.invoices}</td>
                        <td className="px-4 py-2 text-right text-green-600">{money(c.paid)}</td>
                        <td className="px-4 py-2 text-right text-red-600">{money(c.outstanding)}</td>
                        <td className="px-4 py-2 text-right font-semibold">{money(c.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      </W>

      {isAdmin && profit && (<W id="profit" title="Expected profit">
        <div className="hp-card p-6" data-testid="profit-section">
          <h2 className="font-heading text-xl font-bold tracking-tight mb-5 flex items-center gap-2">
            <TrendUp size={20} className="text-primary" weight="duotone" /> Expected Profit
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <StatCard testid="profit-sales" label="Selling Value" value={money(profit.total_sales)} icon={CurrencyDollar} />
            <StatCard testid="profit-cost" label="Purchase Cost" value={money(profit.total_cost)} icon={Factory} tone="gold" />
            <StatCard testid="profit-expected" label="Expected Profit" value={money(profit.expected_profit)} icon={TrendUp} tone="green" />
            <StatCard testid="profit-avg-margin" label="Avg Profit %" value={`${profit.avg_margin ?? 0}%`} icon={Percent} tone="green" />
          </div>
          {profit.monthly && profit.monthly.some((m) => m.sales || m.cost) && (
            <div className="mb-6" data-testid="profit-trend">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Profit trend (last 12 months)</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={profit.monthly} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e4e4e7" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#e4e4e7" }} />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={64} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)} />
                  <Tooltip formatter={(v, n) => [money(v), n]} cursor={{ fill: "rgba(34,197,94,0.06)" }} />
                  <Bar dataKey="profit" name="Profit" fill="#16a34a" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {profit.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No profit to show yet. Link a Purchase Order (cost price) to an order to compare against its selling price.</p>
          ) : (
            <div className="border border-border rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider font-semibold text-muted-foreground bg-muted/40">
                    <th className="px-4 py-2 font-semibold">Order</th>
                    <th className="px-4 py-2 font-semibold">Client</th>
                    <th className="px-4 py-2 font-semibold text-right">Selling</th>
                    <th className="px-4 py-2 font-semibold text-right">Cost</th>
                    <th className="px-4 py-2 font-semibold text-right">Profit</th>
                    <th className="px-4 py-2 font-semibold text-right">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllProfit ? profit.rows : profit.rows.slice(0, 10)).map((r, i) => (
                    <tr key={r.order_id} className={`cursor-pointer hover:bg-primary/5 ${i % 2 ? "bg-muted/30" : ""}`} onClick={() => navigate(`/orders/${r.order_id}`)} data-testid={`profit-row-${r.order_number}`}>
                      <td className="px-4 py-2 font-medium">{r.order_number}</td>
                      <td className="px-4 py-2">{r.client_name}</td>
                      <td className="px-4 py-2 text-right">{money(r.sales)}</td>
                      <td className="px-4 py-2 text-right text-amber-600">{money(r.cost)}</td>
                      <td className={`px-4 py-2 text-right font-semibold ${r.profit >= 0 ? "text-green-600" : "text-red-600"}`}>{money(r.profit)}</td>
                      <td className="px-4 py-2 text-right text-muted-foreground">{r.margin}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {profit.rows.length > 10 && (
                <div className="border-t border-border p-3 text-center">
                  <Button variant="ghost" size="sm" onClick={() => setShowAllProfit((v) => !v)} data-testid="profit-show-more">
                    {showAllProfit ? "Show less" : `Show ${profit.rows.length - 10} more`}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </W>)}

      {isAdmin && shipModes && (<W id="export" title="Export method mix">
        <div className="hp-card p-6" data-testid="export-mode-section">
          <h2 className="font-heading text-xl font-bold tracking-tight mb-1 flex items-center gap-2">
            <Truck size={20} className="text-primary" weight="duotone" /> Export Method Mix
          </h2>
          <p className="text-sm text-muted-foreground mb-5">Courier vs Air Cargo shipments dispatched · Brazil Courier is counted as air cargo.</p>
          {shipModes.total === 0 ? (
            <p className="text-sm text-muted-foreground">No dispatch jobs yet.</p>
          ) : (
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={[{ name: "Courier", value: shipModes.courier }, { name: "Air Cargo", value: shipModes.air_cargo }]}
                    dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}
                  >
                    <Cell fill="#1E7FD4" />
                    <Cell fill="#F5B921" />
                  </Pie>
                  <Tooltip formatter={(v, n) => [`${v} shipment(s)`, n]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-4">
                <StatCard testid="export-courier" label="Courier" value={`${shipModes.courier} · ${shipModes.courier_pct}%`} icon={Truck} />
                <StatCard testid="export-air" label="Air Cargo (incl. Brazil)" value={`${shipModes.air_cargo} · ${shipModes.air_cargo_pct}%`} icon={PaperPlaneTilt} tone="gold" />
              </div>
            </div>
          )}
          {shipModes.monthly?.some((m) => m.courier || m.air_cargo) && (
            <div className="mt-6" data-testid="export-mode-trend">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Trend (last 12 months)</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={shipModes.monthly} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e4e4e7" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#e4e4e7" }} />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={40} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "rgba(30,127,212,0.06)" }} />
                  <Legend />
                  <Bar dataKey="courier" name="Courier" stackId="a" fill="#1E7FD4" />
                  <Bar dataKey="air_cargo" name="Air Cargo" stackId="a" fill="#F5B921" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </W>)}

      {isAdmin && topItems.length > 0 && (<W id="top-items" title="Top selling products">
        <div className="hp-card p-6" data-testid="top-items-section">
          <h2 className="font-heading text-xl font-bold tracking-tight mb-1 flex items-center gap-2">
            <Pill size={20} className="text-primary" weight="duotone" /> Top Selling Products
          </h2>
          <p className="text-sm text-muted-foreground mb-5">Ranked by total quantity sold across all issued invoices.</p>
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <ResponsiveContainer width="100%" height={Math.max(220, topItems.length * 44)}>
              <BarChart layout="vertical" data={topItems} margin={{ top: 0, right: 24, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e4e4e7" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={150} />
                <Tooltip formatter={(v, n) => (n === "quantity" ? [`${v} units`, "Qty sold"] : [money(v), n])} cursor={{ fill: "rgba(30,127,212,0.06)" }} />
                <Bar dataKey="quantity" name="quantity" fill="#1E7FD4" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="border border-border rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider font-semibold text-muted-foreground bg-muted/40">
                    <th className="px-4 py-2 font-semibold">Product</th>
                    <th className="px-4 py-2 font-semibold text-right">Qty sold</th>
                    <th className="px-4 py-2 font-semibold text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {topItems.map((it, i) => (
                    <tr key={it.name} className={i % 2 ? "bg-muted/30" : ""} data-testid={`top-item-row-${it.name}`}>
                      <td className="px-4 py-2 font-medium">{it.name}</td>
                      <td className="px-4 py-2 text-right font-semibold">{it.quantity}</td>
                      <td className="px-4 py-2 text-right text-muted-foreground">{money(it.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </W>)}

      {!isTeam && (<W id="ready" title="Ready to ship">
        <div data-testid="client-ready-section">
          <div className="mb-3">
            <h2 className="font-heading text-xl font-bold tracking-tight">{t("dash.readyTitle")}</h2>
            <p className="text-xs text-muted-foreground mt-1">{t("dash.readyHint")}</p>
          </div>
          <div className="hp-card overflow-hidden">
            <table className="w-full text-sm table-fixed">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider font-semibold text-muted-foreground bg-muted/40">
                  <th className="px-3 sm:px-4 py-3 font-semibold w-[20%]">{t("common.order")}<span className="hidden sm:inline"> / {t("common.shipment")}</span></th>
                  <th className="px-3 sm:px-4 py-3 font-semibold w-[20%]">PTN</th>
                  <th className="px-3 sm:px-4 py-3 font-semibold">{t("common.items")}</th>
                  <th className="px-2 sm:px-4 py-3 font-semibold text-right w-[30%] sm:w-[20%]">{t("common.status")}</th>
                </tr>
              </thead>
              <tbody>
                {readyRows.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">{t("dash.readyNone")}</td></tr>
                ) : (readyAll ? readyRows : readyRows.slice(0, 5)).map((r, i) => (
                  <tr key={r.id} className={`${i % 2 ? "bg-muted/30" : ""} cursor-pointer hover:bg-primary/5 align-top`} onClick={() => navigate(r.link)} data-testid={`ready-row-${r.shipment_number || r.order_number}`}>
                    <td className="px-3 sm:px-4 py-3 break-words"><span className="font-medium">{r.order_number}</span>{r.shipment_number && <span className="block text-primary text-xs">{r.shipment_number}</span>}</td>
                    <td className="px-3 sm:px-4 py-3 break-words" data-testid={`ready-ptn-${r.shipment_number || r.order_number}`}>{r.ptn || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 sm:px-4 py-3 text-muted-foreground break-words">{r.items.map((it) => `${it.quantity}× ${it.name}`).join(", ") || "—"}</td>
                    <td className="px-2 sm:px-4 py-3 text-right">
                      {(() => {
                        const STAGE = {
                          ready: { label: t("dash.ready"), cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: <CheckCircle size={12} weight="fill" /> },
                          preparing: { label: t("dash.preparing"), cls: "bg-amber-50 text-amber-700 border-amber-200", icon: <ClockCountdown size={12} /> },
                          awaiting_confirmation: { label: t("dash.awaitingConfirmation"), cls: "bg-orange-50 text-orange-700 border-orange-200", icon: <ClockCountdown size={12} /> },
                          awaiting_dispatch: { label: t("dash.awaitingDispatch"), cls: "bg-sky-50 text-sky-700 border-sky-200", icon: <PaperPlaneTilt size={12} weight="fill" /> },
                          on_hold: { label: t("dash.onHold"), cls: "bg-red-50 text-red-700 border-red-200", icon: <ClockCountdown size={12} /> },
                        };
                        const st = STAGE[r.stage] || STAGE.preparing;
                        return (
                          <span data-testid={`ready-badge-${r.shipment_number || r.order_number}`} className={`inline-flex items-center gap-1 rounded-sm border px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-left max-w-full break-words ${st.cls}`}>
                            {st.icon} {st.label}
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {readyRows.length > 5 && (
              <div className="px-4 py-2 border-t border-border bg-muted/20 text-center">
                <Button variant="ghost" size="sm" className="rounded-sm h-8 text-primary" onClick={() => setReadyAll((v) => !v)} data-testid="ready-toggle">
                  {readyAll ? "Show less" : `Show ${readyRows.length - 5} more`}
                </Button>
              </div>
            )}
          </div>
        </div>
      </W>)}

      <W id="recent" title="Recent orders">
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-bold tracking-tight">{t("dash.recentOrders")}</h2>
          <button onClick={() => navigate("/orders")} className="text-sm text-primary hover:underline" data-testid="view-all-orders">
            {t("common.viewAll")}
          </button>
        </div>
        <div className="border border-border rounded-md overflow-x-auto bg-card">
          <table className="w-full text-sm table-fixed md:table-auto">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider font-semibold text-muted-foreground bg-muted/40">
                <th className="px-3 md:px-4 py-3 font-semibold w-[36%] md:w-auto">{t("common.order")}</th>
                {isTeam && <th className="px-3 md:px-4 py-3 font-semibold hidden md:table-cell">{t("common.client")}</th>}
                <th className="px-3 md:px-4 py-3 font-semibold w-[28%] md:w-auto">{t("common.total")}</th>
                <th className="px-3 md:px-4 py-3 font-semibold w-[36%] md:w-auto">{t("common.status")}</th>
                <th className="px-3 md:px-4 py-3 font-semibold hidden md:table-cell">{t("common.placed")}</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o, i) => (
                <tr
                  key={o.id}
                  onClick={() => navigate(`/orders/${o.id}`)}
                  className={`cursor-pointer hover:bg-primary/5 transition-colors ${i % 2 ? "bg-muted/30" : ""}`}
                  data-testid={`dash-order-row-${o.order_number}`}
                >
                  <td className="px-3 md:px-4 py-3 font-medium break-words">{o.order_number}<div className="md:hidden text-xs text-muted-foreground font-normal">{isTeam && o.client_name ? <div className="truncate">{o.client_name}</div> : null}{fmtDate(o.created_at)}</div></td>
                  {isTeam && <td className="px-3 md:px-4 py-3 hidden md:table-cell">{o.client_name}</td>}
                  <td className="px-3 md:px-4 py-3 tabular-nums break-words">{money(o.total)}</td>
                  <td className="px-3 md:px-4 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-3 md:px-4 py-3 text-muted-foreground hidden md:table-cell">{fmtDate(o.created_at)}</td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">{t("dash.noOrders")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </W>
      </DashboardWidgets>
    </div>
  );
}
